import { hasClass } from '@opten/dom/has-class';
import { removeClass } from '@opten/dom/remove-class';
import { toggleClass } from '@opten/dom/toggle-class';

export interface FilterHelperOptions {
	filterControlSelector?: string;
	filterFormSelector?: string;
	onSubmit?: () => void;
	onReset?: () => void;
	onRemoveFilter?: (value, name) => void;
}

export class FilterHelper {
	static defaults: FilterHelperOptions = {
		filterControlSelector: '.js-filter-control',
		filterFormSelector: '.js-filter-form',
		onSubmit: () => {},
		onReset: () => {},
		onRemoveFilter: (value, name) => {},
	};

	form: HTMLFormElement;
	emptyState: HTMLElement;
	tagContainer: HTMLElement;
	filters: HTMLInputElement[];
	search: HTMLInputElement;

	private filterGroups: any = {};

	options: FilterHelperOptions;

	tags: HTMLElement[] = [];
	private tagTemplate: HTMLElement;

	private modals: HTMLElement[];

	constructor(private element: HTMLElement, options?: FilterHelperOptions) {
		this.options = { ...FilterHelper.defaults, ...options };

		this.form = hasClass(
			element,
			this.options.filterFormSelector.substring(1)
		)
			? (element as HTMLFormElement)
			: (element.querySelector(
					this.options.filterFormSelector
			  ) as HTMLFormElement);
		this.emptyState = element.getElementsByClassName(
			'js-filter-empty-state'
		)[0] as HTMLElement;
		this.modals = Array.from(
			element.getElementsByClassName('js-modal')
		) as HTMLElement[];
		this.filters = Array.from(
			element.querySelectorAll(this.options.filterControlSelector)
		) as HTMLInputElement[];
		this.search = element.getElementsByClassName(
			'js-filter-search'
		)[0] as HTMLInputElement;
		this.tagContainer = element.getElementsByClassName(
			'js-filter-tag-container'
		)[0] as HTMLElement;
		this.tagTemplate = element.getElementsByClassName(
			'js-filter-tag'
		)[0] as HTMLElement;

		this.form.addEventListener('reset', (e) => {
			this.closeModal();
			this.tags.forEach((tag) => {
				this.removeFilterItem(tag);
			});
			if (this.search) {
				this.search.value = '';
			}
			this.toggleEmptyState(false);
			this.options.onReset();
		});

		this.form.addEventListener('submit', (e) => {
			e.preventDefault();
			this.closeModal();
			this.options.onSubmit();
		});

		this.filters.forEach((filter) => {
			if (filter.type === 'checkbox') {
				const self = filter.getAttribute('data-self');
				const connected = filter.getAttribute('data-connected');
				if (connected) {
					if (this.filterGroups[connected]) {
						this.filterGroups[connected].push(filter);
					} else {
						this.filterGroups[connected] = [filter];
					}
				}
				const filterSame = this.filters.filter(
					(o) => o.name === filter.name
				);
				const filterAll = filterSame.filter((o) => o.value === '');
				filter.addEventListener('change', (e) => {
					if (this.filterGroups[self]) {
						this.filterGroups[self].filter(
							(o) => (o.checked = filter.checked)
						);
					}
					if (connected) {
						const target = this.filters.find(
							(o) => o.getAttribute('data-self') === connected
						);
						if (target) {
							if (filter.checked) {
								target.checked = this.filters
									.filter(
										(o) =>
											o.getAttribute('data-connected') ===
											connected
									)
									.every((o) => o.checked);
							} else {
								target.checked = false;
							}
						}
					}
					if (filter.value === '' && filter.checked) {
						filterSame.filter((o) => (o.checked = false));
					}
					if (filterAll && filterAll.length > 0) {
						filterAll.forEach((all) => {
							all.checked =
								filterSame.some(
									(o) =>
										o.checked ||
										(o.checked && o.value === '')
								) === false;
						});
					}
				});
			}
		});
	}

	clear() {
		this.filters.forEach(filter => {
			if (filter.checked) {
				filter.checked = false;
			}
			const options = Array.from(filter.options || []);
			if (options && options.length > 0) {
				options.forEach(option => {
					option.selected = false;
				});
			}
		});
		if (this.search) {
			this.search.value = '';
		}
	}

	updateTags() {
		if (this.tagContainer) {
			this.tags = [];
			this.tagContainer.innerHTML = '';
			const data = new FormData(this.form);
			for (const [name, value] of data) {
				if (value && value !== '') {
					const filter = this.filters.find(
						(o) => o.name === name && o.value === value
					);
					const title = filter
						? filter.getAttribute('data-title')
						: '';
					if (title) {
						this.addFilterItem(title, name, value);
					}
				}
			}
			toggleClass(this.element, 'has-filters', this.tags.length > 0);
		}
	}

	addFilterItem(title, name, value) {
		const temp = document.createElement('div');
		let tag = this.tagTemplate.cloneNode(true) as HTMLElement;
		removeClass(tag, 'd-none');
		tag.setAttribute('data-name', name);
		tag.setAttribute('data-value', value);
		temp.appendChild(tag);
		tag.outerHTML = tag.outerHTML.replace(/{title}/g, title);
		tag = (temp.firstChild || temp.firstElementChild) as HTMLElement;
		this.tagContainer.appendChild(tag);
		this.tags.push(tag);

		tag.addEventListener('click', (e) => {
			e.preventDefault();
			this.removeFilterItem(tag);
		});
	}

	removeFilterItem(tag) {
		const name = tag.getAttribute('data-name');
		const value = tag.getAttribute('data-value');
		const filter = this.filters.find(
			(o) => o.name === name && o.value === value
		);
		if (filter) {
			filter.value = filter.defaultValue;
			filter.checked = false;
		}
		this.options.onRemoveFilter(value, name);
	}

	toggleEmptyState(show = false) {
		if (this.emptyState) {
			toggleClass(this.emptyState, 'd-none', show);
		}
	}

	private closeModal() {
		if (this.modals.length) {
			this.modals.forEach((modal) => {
				removeClass(modal, 'is-open');
			});
		}
	}
}
